import { RateCardOptions, RateCardOptionsQuery, TypedListResponse } from '@skyrise-eng/types';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import { TAG } from '@/constants';
import { buildQueryString } from '@/utils';
import { GetRateCardListQuery, RateCard } from '@/models';

const RATE_CARD_URL = 'rate-cards/api/v1';

// Define a service using a base URL and expected endpoints
export const rateCardApi = createApi({
  reducerPath: 'rateCardApi',
  tagTypes: [TAG.RATE_CARD, TAG.RATE_CARD_OPTIONS],
  baseQuery,
  endpoints: (builder) => ({
    getRateCardList: builder.query<TypedListResponse<RateCard>, GetRateCardListQuery>({
      query: (params) => `${RATE_CARD_URL}?${buildQueryString(params)}`,
      providesTags: () => [TAG.RATE_CARD]
    }),
    getRateCardOptions: builder.query<RateCardOptions, RateCardOptionsQuery & { showDraft?: boolean }>({
      query: (params) =>
        `${RATE_CARD_URL}/options?${buildQueryString({ ...params, showDraft: !!params.showDraft })}`,
      providesTags: () => [TAG.RATE_CARD_OPTIONS]
    }),
    deleteRateCard: builder.mutation<RateCard, string>({
      query: (id) => ({
        url: `${RATE_CARD_URL}/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [TAG.RATE_CARD, TAG.RATE_CARD_OPTIONS]
    }),
    updateRateCard: builder.mutation<RateCard, RateCard>({
      query: ({ _id, ...rest }) => ({
        url: `${RATE_CARD_URL}/${_id}`,
        method: 'PUT',
        body: rest
      }),
      invalidatesTags: [TAG.RATE_CARD, TAG.RATE_CARD_OPTIONS]
    }),
    createRateCard: builder.mutation<RateCard, Omit<RateCard, '_id'>>({
      query: (rateCard) => ({
        url: RATE_CARD_URL,
        method: 'POST',
        body: rateCard
      }),
      invalidatesTags: [TAG.RATE_CARD, TAG.RATE_CARD_OPTIONS]
    })
  })
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetRateCardListQuery,
  useGetRateCardOptionsQuery,
  useDeleteRateCardMutation,
  useUpdateRateCardMutation,
  useCreateRateCardMutation
} = rateCardApi;
